import { useCallback, useEffect, useRef } from "react";

/** How long after a confirm opens a tap on its submit is read as the opener's double-tap. */
export const OPEN_GRACE_MS = 400;

/**
 * A confirm's submit must be pressed on purpose, not by the second half of
 * the press that opened it (the house rule: "a double-click is two clicks",
 * #325). At phone width a dialog's full-width submit can sit right under
 * the opener, so the second click of a double-click — or the second tap of
 * a double-tap — lands on it and submits a confirm nobody read.
 *
 * Shared by every confirm that spends, destroys or decides (Investing's
 * dialogs, Home videos' Analyse / Confirm all / Delete) — it moved here
 * from components/investing when the second domain needed it.
 *
 * Spread the returned handlers on the submit button:
 *   - a click with `detail > 1` (the second of a double-click) is refused,
 *     and its mousedown is prevented so it can't steal focus either;
 *   - a touch tap within OPEN_GRACE_MS of the dialog opening is refused
 *     (a double-tap's taps can each report `detail` 1);
 *   - keyboard activation (`detail` 0) always acts.
 */
export default function useSubmitGuard(open) {
  const openedAtRef = useRef(0);
  const touchRef = useRef(false);

  useEffect(() => {
    if (open) openedAtRef.current = Date.now();
  }, [open]);

  const onPointerDown = useCallback((event) => {
    touchRef.current = event.pointerType === "touch";
  }, []);

  const onMouseDown = useCallback((event) => {
    if (event.detail > 1) event.preventDefault();
  }, []);

  const onClick = useCallback((event) => {
    const touched = touchRef.current;
    touchRef.current = false;
    const doubled = event.detail > 1;
    const tooSoon = touched && event.detail > 0 && Date.now() - openedAtRef.current < OPEN_GRACE_MS;
    if (doubled || tooSoon) event.preventDefault();
  }, []);

  return { onPointerDown, onMouseDown, onClick };
}
